import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ContentSwitcher, Switch } from '@carbon/react';
import { Calendar, Checkmark } from '@carbon/react/icons';
import { DashboardDateRange } from '@restavo/shared/enums/dashboard';
import { windowForCustomRange, windowForPreset } from 'src/utils/dashboardRange';
import RangeCalendar, { CalendarDay } from './RangeCalendar';

const PRESETS = [
  { label: 'today', range: DashboardDateRange.DAILY },
  { label: 'yesterday', range: DashboardDateRange.YESTERDAY },
  { label: 'weekly', range: DashboardDateRange.WEEKLY },
  { label: 'monthly', range: DashboardDateRange.MONTHLY },
  { label: 'yearly', range: DashboardDateRange.YEARLY },
  { label: 'all', range: DashboardDateRange.ALL },
];

interface DateRangeMenuProps {
  startDate: Date | null;
  endDate: Date;
  dateRange: DashboardDateRange;
  timezone: string;
  onChange: (startDate: Date | null, endDate: Date, dateRange: DashboardDateRange) => void;
}

type Staged = { range: DashboardDateRange; start: CalendarDay | null; end: CalendarDay | null };

/** An instant as the restaurant's calendar day. */
const toDay = (date: Date, timezone: string): CalendarDay => dayjs(date).tz(timezone).format('YYYY-MM-DD');

/**
 * The dashboards' date filter: a trigger styled like the dashboard tabs beside it, opening a
 * panel with the presets on top, a two-month range calendar that is always visible, and
 * Cancel / Apply.
 *
 * Everything stages until Apply — a preset only previews its range on the calendar. One rule
 * for both paths, and no refetch fires while the operator is still deciding. Picking days on
 * the calendar turns the selection into a custom range; picking a preset again returns to it.
 *
 * Days are handled as the RESTAURANT's calendar days (`YYYY-MM-DD`) and only turned back into
 * instants on Apply, through the same windowForPreset / windowForCustomRange the rest of the
 * dashboard uses — so "15 Sep" means 15 Sep in the restaurant's timezone whatever the browser's.
 *
 * The trigger's label IS the committed state, so the active range is readable when closed.
 */
const DateRangeMenu = React.memo((props: DateRangeMenuProps) => {
  const { t, i18n } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [month, setMonth] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const today = dayjs().tz(props.timezone).format('YYYY-MM-DD');

  // Close on an outside click. Without it the panel survives navigation to another tab and
  // hangs over the dashboard it was meant to filter.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocumentClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  /** The two visible months end at the range's end, or today if that comes first. */
  const monthsEndingAt = useCallback(
    (end: CalendarDay | null) => {
      const anchor = end && end < today ? end : today;
      return dayjs(anchor).subtract(1, 'month').format('YYYY-MM');
    },
    [today]
  );

  const stagePreset = useCallback(
    (range: DashboardDateRange) => {
      const window = windowForPreset(range, props.timezone);
      const next = {
        range,
        start: window.startDate ? toDay(window.startDate, props.timezone) : null,
        end: toDay(window.endDate, props.timezone),
      };
      setStaged(next);
      setMonth(monthsEndingAt(next.end));
    },
    [props.timezone, monthsEndingAt]
  );

  const openPanel = () => {
    const current: Staged = {
      range: props.dateRange,
      start: props.startDate ? toDay(props.startDate, props.timezone) : null,
      end: toDay(props.endDate, props.timezone),
    };
    setStaged(current);
    setMonth(monthsEndingAt(current.end));
    setOpen(true);
  };

  const apply = () => {
    if (!staged) {
      return;
    }
    if (staged.range === DashboardDateRange.CUSTOM) {
      if (!staged.start) {
        return;
      }
      // A single picked day is a one-day range.
      const { startDate, endDate } = windowForCustomRange(
        dayjs(staged.start).toDate(),
        dayjs(staged.end ?? staged.start).toDate(),
        props.timezone
      );
      props.onChange(startDate, endDate, DashboardDateRange.CUSTOM);
    } else {
      // Recomputed now, not reused from staging: the panel may have sat open past midnight.
      const window = windowForPreset(staged.range, props.timezone);
      props.onChange(window.startDate, window.endDate, staged.range);
    }
    setOpen(false);
  };

  const label = () => {
    if (props.dateRange === DashboardDateRange.CUSTOM && props.startDate) {
      return `${dayjs(props.startDate).tz(props.timezone).format('D MMM')} – ${dayjs(props.endDate)
        .tz(props.timezone)
        .format('D MMM')}`;
    }
    const preset = PRESETS.find((item) => item.range === props.dateRange);
    return t(`translation:dateRanges.${preset?.label ?? 'today'}`);
  };

  return (
    <div className="dashboard-date-menu" ref={containerRef}>
      <button
        type="button"
        className="dashboard-date-menu__trigger"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Calendar size={16} aria-hidden />
        <span>{label()}</span>
      </button>
      {open && staged && (
        // A non-modal <dialog open>: the panel sits in the page flow beside the trigger, and
        // the outside-click handler above is what closes it.
        <dialog open className="dashboard-date-menu__panel" aria-label={t('dateRange')}>
          {/* -1 selects nothing: a custom range is picked on the calendar, not by a preset. */}
          <ContentSwitcher
            size="md"
            aria-label={t('dateRange')}
            selectedIndex={PRESETS.findIndex((item) => item.range === staged.range)}
            onChange={({ index }: { index: number }) => stagePreset(PRESETS[index].range)}
          >
            {PRESETS.map((item) => (
              <Switch key={item.range} name={item.label} text={t(`translation:dateRanges.${item.label}`)} />
            ))}
          </ContentSwitcher>
          <RangeCalendar
            month={month}
            onMonthChange={setMonth}
            start={staged.start}
            end={staged.end}
            onChange={(start, end) => setStaged({ range: DashboardDateRange.CUSTOM, start, end })}
            today={today}
            locale={i18n.language}
            labels={{ previousMonth: t('previousMonth'), nextMonth: t('nextMonth') }}
          />
          <div className="dashboard-date-menu__footer">
            <Button kind="secondary" size="md" onClick={() => setOpen(false)}>
              {t('translation:actions.cancel')}
            </Button>
            <Button
              kind="primary"
              size="md"
              renderIcon={Checkmark}
              onClick={apply}
              disabled={staged.range === DashboardDateRange.CUSTOM && !staged.start}
            >
              {t('translation:actions.apply')}
            </Button>
          </div>
        </dialog>
      )}
    </div>
  );
});

DateRangeMenu.displayName = 'DateRangeMenu';

export default DateRangeMenu;
